import { useEffect, useMemo, useState } from "react";
import { fetchFPLData, fetchFixturesByGameweek } from "../api/fplApi";
import { FaSpinner, FaTimes, FaChartLine, FaUsers, FaCoins, FaCalendarAlt } from "react-icons/fa";

const Suggestion = ({ compact = false }) => {
  const [suggestedPlayers, setSuggestedPlayers] = useState([]);
  const [targetGameweek, setTargetGameweek] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    const fetchPredictionData = async () => {
      setLoading(true);
      const fplData = await fetchFPLData();
      if (!fplData) {
        setLoading(false);
        return;
      }

      const players = fplData.elements || [];
      const teams = fplData.teams || [];
      const events = fplData.events || [];
      const target = events.find((gw) => gw.is_next) || events.find((gw) => gw.is_current) || events[0];
      const gwId = target?.id || 1;
      setTargetGameweek(gwId);

      const fixtures = await fetchFixturesByGameweek(gwId);
      const predictions = calculatePredictedScores(players, teams, fixtures);
      setSuggestedPlayers(predictions.slice(0, compact ? 4 : 18));
      setLoading(false);
    };

    fetchPredictionData();
  }, [compact]);

  const calculatePredictedScores = (players, teams, fixtures) => {
    return players
      .filter((p) => p.status !== "u" && Number(p.chance_of_playing_next_round ?? 100) > 0)
      .map((p) => {
        const team = teams.find((t) => t.id === p.team);
        const nextFixture = fixtures.find((f) => f.team_h === p.team || f.team_a === p.team);
        if (!nextFixture) return null;

        const isHome = nextFixture.team_h === p.team;
        const oppId = isHome ? nextFixture.team_a : nextFixture.team_h;
        const opp = teams.find((t) => t.id === oppId);
        const difficulty = Number(isHome ? nextFixture.team_h_difficulty : nextFixture.team_a_difficulty) || 3;
        const form = Number(p.form || 0);
        const xg = Number(p.expected_goals || 0);
        const xa = Number(p.expected_assists || 0);
        const xgi = Number(p.expected_goal_involvements || 0);
        const pointsPerGame = Number(p.points_per_game || 0);
        const minutes = Number(p.minutes || 0);
        const availability = Number(p.chance_of_playing_next_round ?? 100) / 100;
        const predictedScore = (
          form * 0.42 +
          pointsPerGame * 0.32 +
          xg * 0.9 +
          xa * 0.75 +
          xgi * 0.35 +
          (6 - difficulty) * 0.55
        ) * availability;

        return {
          ...p,
          id: p.id,
          name: p.web_name,
          teamName: team?.name || "Unknown",
          teamShort: team?.short_name || "",
          teamCode: p.team_code,
          nextOpponent: `${opp?.name || "Unknown"} (${isHome ? "H" : "A"})`,
          difficulty,
          predictedScore: Number(Math.max(0, predictedScore).toFixed(1)),
          minutes,
          form,
          pointsPerGame,
        };
      })
      .filter(Boolean)
      .sort((a, b) => b.predictedScore - a.predictedScore);
  };

  const position = (type) => ({ 1: "Goalkeeper", 2: "Defender", 3: "Midfielder", 4: "Forward" }[type] || "Player");
  const price = (p) => `£${(Number(p.now_cost || 0) / 10).toFixed(1)}m`;
  const selectedPct = (p) => `${Number(p.selected_by_percent || 0).toFixed(1)}%`;
  const modalStats = useMemo(() => selected ? [
    ["Form", selected.form],
    ["Points / match", selected.pointsPerGame],
    ["Total points", selected.total_points ?? 0],
    ["Ownership", selectedPct(selected)],
    ["Price", price(selected)],
    ["Minutes", selected.minutes ?? 0],
    ["Goals", selected.goals_scored ?? 0],
    ["Assists", selected.assists ?? 0],
    ["Clean sheets", selected.clean_sheets ?? 0],
    ["xGI", Number(selected.expected_goal_involvements || 0).toFixed(2)],
  ] : [], [selected]);

  return (
    <div className={`w-full ${compact ? "" : "fpl-picks-page"}`}>
      {!compact && (
        <div className="fpl-page-intro-card fpl-picks-intro">
          <div>
            <span className="fpl-eyebrow">FPL NEPAL MODEL</span>
            <h2>{targetGameweek ? `Top Picks for GW${targetGameweek}` : "Top Picks"}</h2>
            <p>Shortlist based on form, expected involvement, fixture difficulty and availability. Click a player for details.</p>
          </div>
          <strong>{suggestedPlayers.length} picks</strong>
        </div>
      )}

      {loading ? (
        <div className="flex justify-center items-center py-10">
          <FaSpinner className="animate-spin text-fuchsia-500 text-3xl" />
        </div>
      ) : (
        <div className={compact ? "grid grid-cols-2 lg:grid-cols-4 gap-3" : "fpl-picks-grid"}>
          {suggestedPlayers.map((p, index) => (
            <button key={p.id} type="button" className="fpl-pick-card fpl-pick-card-button" onClick={() => setSelected(p)}>
              {!compact && <span className="fpl-pick-rank">#{index + 1}</span>}
              <div className="relative w-full h-28 sm:h-32 flex justify-center items-center bg-slate-50">
                <img
                  src={`https://fantasy.premierleague.com/dist/img/shirts/standard/shirt_${p.teamCode}-220.webp`}
                  alt={`${p.name} kit`}
                  className="h-full w-auto object-contain"
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.src = "https://fantasy.premierleague.com/dist/img/shirts/standard/shirt_1-220.webp";
                  }}
                />
                <span className="absolute bottom-2 right-2 bg-emerald-600 text-white text-[11px] px-2 py-1 rounded-full font-bold">
                  {p.predictedScore} pts
                </span>
              </div>
              <div className="fpl-pick-info">
                <p className="fpl-pick-name">{p.name}</p>
                <p className="fpl-pick-meta">{p.teamShort || p.teamName} · {position(p.element_type)} · {price(p)}</p>
                <div className="fpl-pick-fixture"><FaCalendarAlt /> {p.nextOpponent} <span>FDR {p.difficulty}</span></div>
                {!compact && (
                  <div className="fpl-pick-mini-stats">
                    <span><b>{p.form}</b> Form</span>
                    <span><b>{p.pointsPerGame}</b> PPG</span>
                    <span><b>{selectedPct(p)}</b> Own</span>
                  </div>
                )}
              </div>
            </button>
          ))}
        </div>
      )}

      {selected && (
        <div className="fpl-player-modal-backdrop" onMouseDown={() => setSelected(null)}>
          <div className="fpl-player-modal" onMouseDown={(e) => e.stopPropagation()}>
            <button className="fpl-player-modal-close" onClick={() => setSelected(null)} aria-label="Close"><FaTimes /></button>
            <div className="fpl-player-modal-hero">
              <img src={`https://fantasy.premierleague.com/dist/img/shirts/standard/shirt_${selected.teamCode}-220.webp`} alt="" />
              <div>
                <span className="fpl-eyebrow">GW{targetGameweek} TOP PICK</span>
                <h3>{selected.name}</h3>
                <p>{selected.teamName} · {position(selected.element_type)}</p>
                <div className="fpl-player-modal-tags">
                  <span><FaCoins /> {price(selected)}</span>
                  <span><FaUsers /> {selectedPct(selected)} owned</span>
                  <span><FaChartLine /> {selected.predictedScore} projected</span>
                </div>
              </div>
            </div>
            <div className="fpl-player-next-fixture">
              <span>Next fixture</span>
              <strong>{selected.nextOpponent}</strong>
              <em>FDR {selected.difficulty}</em>
            </div>
            <div className="fpl-player-stat-grid">
              {modalStats.map(([label, value]) => (
                <div key={label}><span>{label}</span><strong>{value}</strong></div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Suggestion;
